import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDroppable, useDraggable } from "@dnd-kit/core";
import { Label } from "../ui/label";
import AutogrowTextarea from "../ui/autogrowtextarea";
import { GripVertical } from "lucide-react";

const SOURCE_CANVAS_SIZE = 300; // Stored coordinates are in 0-300 space

export interface MatrixPoint {
  id: string;
  x: number;
  y: number;
  quadrant: string;
  quadrantLabel: string;
  feedback?: string;
  label?: string;
  listItemId?: number | string;
  sourceListKey?: string;
}

/** Legacy single-point value (pre multi-point) */
interface MatrixValueLegacy {
  x: number;
  y: number;
  quadrant?: string;
  quadrantLabel?: string;
  feedback?: string;
}

export interface MatrixGaugeValue {
  points: MatrixPoint[];
}

interface QuadrantConfig {
  label: string;
  color: string;
  prompt?: string;
}

interface PresetConfig {
  xAxisLabel?: string;
  yAxisLabel?: string;
  xLowLabel?: string;
  xHighLabel?: string;
  yLowLabel?: string;
  yHighLabel?: string;
  q1Label?: string;
  q2Label?: string;
  q3Label?: string;
  q4Label?: string;
}

interface MatrixGaugeProps {
  itemkey: string;
  compact?: boolean;
  readOnly?: boolean;
  content: {
    key: string;
    label?: string;
    xAxisLabel?: string;
    yAxisLabel?: string;
    xLowLabel?: string;
    xHighLabel?: string;
    yLowLabel?: string;
    yHighLabel?: string;
    q1Label?: string;
    q2Label?: string;
    q3Label?: string;
    q4Label?: string;
    q1Prompt?: string;
    q2Prompt?: string;
    q3Prompt?: string;
    q4Prompt?: string;
    showCoordinates?: boolean;
    showFeedback?: boolean;
    showPointLabels?: boolean;
    showPointsLegend?: boolean;
    preset?: "eisenhower" | "effort_impact" | "risk_reward" | "custom" | "";
    linkedListKeys?: string[];
  };
  onChange?: (key: string, value: MatrixGaugeValue) => void;
  defaultValue?: MatrixGaugeValue | MatrixValueLegacy;
  value?: MatrixGaugeValue | MatrixValueLegacy;
  /** When set, gauge shows these list items; user drags from here onto the matrix */
  connectedListKey?: string;
  connectedListItems?: Array<{ id: number; text: string; [key: string]: unknown }>;
  /** Called with (gaugeKey, x, y) in 0–300 space while a list item is dragged over the matrix; used to place the point at drop position */
  onDragOverPosition?: (gaugeKey: string, x: number, y: number) => void;
  /** When set, select this point (e.g. after a drop) and show feedback; cleared via onDidSelectPoint */
  selectPointIdWhenAdded?: string | null;
  onDidSelectPoint?: () => void;
}

export function genPointId(): string {
  return `point-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/** Normalize legacy single-point or partial value to MatrixGaugeValue. Rounds point coordinates to integers. */
export function normalizeGaugeValue(
  val: MatrixGaugeValue | MatrixValueLegacy | undefined | null
): MatrixGaugeValue {
  if (!val) return { points: [] };
  if ("points" in val && Array.isArray(val.points) && val.points.length > 0) {
    const points = val.points.map((p) => ({
      ...p,
      x: Math.round(Number(p.x)),
      y: Math.round(Number(p.y)),
    }));
    return { points };
  }
  const v = val as MatrixValueLegacy;
  if (typeof v.x === "number" && typeof v.y === "number") {
    const x = Math.round(v.x);
    const y = Math.round(v.y);
    const quad = inferQuadrant(x, y);
    return {
      points: [
        {
          id: genPointId(),
          x,
          y,
          quadrant: quad.quadrant,
          quadrantLabel: quad.label,
          feedback: v.feedback,
        },
      ],
    };
  }
  return { points: [] };
}

export function inferQuadrant(
  x: number,
  y: number,
  size: number = SOURCE_CANVAS_SIZE
): { quadrant: string; label: string } {
  const mid = size / 2;
  if (y >= mid && x < mid) return { quadrant: "Q1", label: "" };
  if (y >= mid && x >= mid) return { quadrant: "Q2", label: "" };
  if (y < mid && x < mid) return { quadrant: "Q3", label: "" };
  return { quadrant: "Q4", label: "" };
}

/** Get quadrant label from gauge content (for use when creating points outside the gauge, e.g. drag-drop). */
export function getQuadrantLabelFromContent(
  quadrant: string,
  content: { q1Label?: string; q2Label?: string; q3Label?: string; q4Label?: string }
): string {
  const key = { Q1: "q1Label", Q2: "q2Label", Q3: "q3Label", Q4: "q4Label" }[quadrant];
  return (key && (content as Record<string, string>)?.[key]) || "";
}

const POINT_COLORS = [
  { border: "#22c55e", label: "#15803d", bg: "rgba(34, 197, 94, 0.15)" },
  { border: "#3b82f6", label: "#1e40af", bg: "rgba(59, 130, 246, 0.15)" },
  { border: "#f59e0b", label: "#b45309", bg: "rgba(245, 158, 11, 0.15)" },
  { border: "#8b5cf6", label: "#6d28d9", bg: "rgba(139, 92, 246, 0.15)" },
  { border: "#ec4899", label: "#be185d", bg: "rgba(236, 72, 153, 0.15)" },
  { border: "#14b8a6", label: "#0d9488", bg: "rgba(20, 184, 166, 0.15)" },
];

/** Draggable list item for "Available items" panel - drag onto matrix */
function ConnectedListItem({
  sourceListKey,
  listItemId,
  text,
  isOnGauge,
}: {
  sourceListKey: string;
  listItemId: number;
  text: string;
  isOnGauge: boolean;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `list-item-${sourceListKey}-${listItemId}`,
    data: {
      type: "LIST_ITEM",
      sourceListKey,
      listItemId,
      text,
    },
  });
  return (
    <div
      ref={setNodeRef}
      className={`flex items-center gap-2 rounded border bg-white px-2 py-1.5 text-xs border-slate-200 min-w-0 ${isOnGauge ? "opacity-60" : ""} ${isDragging ? "opacity-50 shadow-md" : ""}`}
    >
      <div
        {...listeners}
        {...attributes}
        className="flex-shrink-0 cursor-grab active:cursor-grabbing p-0.5 rounded hover:bg-slate-100"
        title={text}
      >
        <GripVertical className="w-3.5 h-3.5 text-slate-400" />
      </div>
      <span className="truncate min-w-0 flex-1">{text || `Item ${listItemId}`}</span>
    </div>
  );
}

const MatrixGauge = ({
  itemkey,
  content,
  onChange,
  defaultValue,
  value: valueProp,
  compact = false,
  readOnly = false,
  connectedListKey,
  connectedListItems,
  onDragOverPosition,
  selectPointIdWhenAdded,
  onDidSelectPoint,
}: MatrixGaugeProps) => {
  const { t, i18n } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const matrixWrapperRef = useRef<HTMLDivElement | null>(null);
  const normalizedDefault = normalizeGaugeValue(defaultValue);
  const normalizedValueProp = valueProp !== undefined && valueProp !== null ? normalizeGaugeValue(valueProp) : null;
  const isControlled = normalizedValueProp !== null;
  const [internalPoints, setInternalPoints] = useState<MatrixPoint[]>(normalizedDefault.points);
  const points = isControlled ? (normalizedValueProp?.points ?? []) : internalPoints;
  const [selectedPointId, setSelectedPointId] = useState<string | null>(null);
  const [hoverPos, setHoverPos] = useState<{ x: number; y: number } | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [draggingPointId, setDraggingPointId] = useState<string | null>(null);
  const dragStartRef = useRef<{ x: number; y: number; pointX: number; pointY: number } | null>(null);

  const canvasSize = compact ? 140 : 260;
  const scaleCoord = (val: number) => (val / SOURCE_CANVAS_SIZE) * canvasSize;
  const toStoredX = (canvasX: number) => Math.round((canvasX / canvasSize) * SOURCE_CANVAS_SIZE);
  const toStoredY = (canvasY: number) => Math.round((canvasY / canvasSize) * SOURCE_CANVAS_SIZE);
  const direction = i18n.dir();

  const presets: Record<string, PresetConfig> = {
    eisenhower: {
      xAxisLabel: t("urgency") || "Urgency",
      yAxisLabel: t("importance") || "Importance",
      xLowLabel: t("not_urgent") || "Not Urgent",
      xHighLabel: t("urgent") || "Urgent",
      yLowLabel: t("not_important") || "Not Important",
      yHighLabel: t("important") || "Important",
      q1Label: t("do_first") || "Do First",
      q2Label: t("schedule") || "Schedule",
      q3Label: t("delegate") || "Delegate",
      q4Label: t("eliminate") || "Eliminate",
    },
    effort_impact: {
      xAxisLabel: t("effort") || "Effort",
      yAxisLabel: t("impact") || "Impact",
      xLowLabel: t("low_effort") || "Low",
      xHighLabel: t("high_effort") || "High",
      yLowLabel: t("low_impact") || "Low",
      yHighLabel: t("high_impact") || "High",
      q1Label: t("quick_wins") || "Quick Wins",
      q2Label: t("major_projects") || "Major Projects",
      q3Label: t("fill_ins") || "Fill Ins",
      q4Label: t("time_wasters") || "Time Wasters",
    },
    risk_reward: {
      xAxisLabel: t("risk") || "Risk",
      yAxisLabel: t("reward") || "Reward",
      xLowLabel: t("low_risk") || "Low",
      xHighLabel: t("high_risk") || "High",
      yLowLabel: t("low_reward") || "Low",
      yHighLabel: t("high_reward") || "High",
      q1Label: t("high_reward_low_risk") || "Best Opportunities",
      q2Label: t("high_reward_high_risk") || "High Stakes",
      q3Label: t("low_reward_low_risk") || "Safe Bets",
      q4Label: t("low_reward_high_risk") || "Avoid",
    },
    custom: {},
  };

  const presetKey = content.preset;
  const isValidPreset = presetKey === "eisenhower" || presetKey === "effort_impact" || presetKey === "risk_reward";
  const preset: PresetConfig = isValidPreset ? presets[presetKey] : {};

  const xAxisLabel = content.xAxisLabel || preset.xAxisLabel || "";
  const yAxisLabel = content.yAxisLabel || preset.yAxisLabel || "";
  const xLowLabel = content.xLowLabel || preset.xLowLabel || "";
  const xHighLabel = content.xHighLabel || preset.xHighLabel || "";
  const yLowLabel = content.yLowLabel || preset.yLowLabel || "";
  const yHighLabel = content.yHighLabel || preset.yHighLabel || "";

  const mainLabel = content.label?.trim() || "";
  const mainLabelLower = mainLabel.toLowerCase();
  const isMainLabelRedundantWithAxis =
    mainLabelLower === xAxisLabel.trim().toLowerCase() ||
    mainLabelLower === yAxisLabel.trim().toLowerCase();
  const showMainLabel = mainLabel.length > 0 && !isMainLabelRedundantWithAxis;

  const quadrantColors: Record<string, { bg: string; border: string; label: string }> = {
    Q1: { bg: "rgba(34, 197, 94, 0.15)", border: "#22c55e", label: "#15803d" },
    Q2: { bg: "rgba(59, 130, 246, 0.15)", border: "#3b82f6", label: "#1e40af" },
    Q3: { bg: "rgba(251, 191, 36, 0.15)", border: "#fbbf24", label: "#b45309" },
    Q4: { bg: "rgba(239, 68, 68, 0.15)", border: "#ef4444", label: "#b91c1c" },
  };

  const getQuadrant = useCallback(
    (x: number, y: number, useSize: number = SOURCE_CANVAS_SIZE): { quadrant: string; label: string; prompt?: string } => {
      const mid = useSize / 2;
      let quadrant: string;
      let quadrantKey: string;
      if (y >= mid && x < mid) {
        quadrant = "Q1";
        quadrantKey = "q1Label";
      } else if (y >= mid && x >= mid) {
        quadrant = "Q2";
        quadrantKey = "q2Label";
      } else if (y < mid && x < mid) {
        quadrant = "Q3";
        quadrantKey = "q3Label";
      } else {
        quadrant = "Q4";
        quadrantKey = "q4Label";
      }
      const label =
        (content[quadrantKey as keyof typeof content] as string) ||
        (preset[quadrantKey as keyof PresetConfig] as string) ||
        "";
      const promptKey = quadrantKey.replace("Label", "Prompt");
      const prompt = content[promptKey as keyof typeof content] as string;
      return { quadrant, label, prompt };
    },
    [content, preset]
  );

  const updatePoints = useCallback(
    (next: MatrixPoint[]) => {
      if (!isControlled) setInternalPoints(next);
      onChange?.(content.key, { points: next });
    },
    [isControlled, onChange, content.key]
  );

  const drawMatrix = useCallback(
    (
      pointsToDraw: MatrixPoint[],
      selectedId: string | null,
      hover: { x: number; y: number } | null
    ) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      ctx.clearRect(0, 0, canvasSize, canvasSize);
      const mid = canvasSize / 2;

      ctx.fillStyle = quadrantColors.Q3.bg;
      ctx.fillRect(0, mid, mid, mid);
      ctx.fillStyle = quadrantColors.Q4.bg;
      ctx.fillRect(mid, mid, mid, mid);
      ctx.fillStyle = quadrantColors.Q1.bg;
      ctx.fillRect(0, 0, mid, mid);
      ctx.fillStyle = quadrantColors.Q2.bg;
      ctx.fillRect(mid, 0, mid, mid);

      ctx.strokeStyle = "#cbd5e1";
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(mid, 0);
      ctx.lineTo(mid, canvasSize);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, mid);
      ctx.lineTo(canvasSize, mid);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.strokeStyle = "#94a3b8";
      ctx.lineWidth = 2;
      ctx.strokeRect(0, 0, canvasSize, canvasSize);

      const quadrantFontSize = compact ? 8 : 10;
      ctx.font = `bold ${quadrantFontSize}px sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      // Convert canvas positions to content coords (high Y = top) so getQuadrant returns the correct quadrant for each drawn position
      const toContent = (canvasX: number, canvasY: number) => ({
        x: (canvasX / canvasSize) * SOURCE_CANVAS_SIZE,
        y: SOURCE_CANVAS_SIZE - (canvasY / canvasSize) * SOURCE_CANVAS_SIZE,
      });
      const c1 = toContent(mid / 2, mid / 2);
      const c2 = toContent(mid + mid / 2, mid / 2);
      const c3 = toContent(mid / 2, mid + mid / 2);
      const c4 = toContent(mid + mid / 2, mid + mid / 2);
      const q1Info = getQuadrant(c1.x, c1.y, SOURCE_CANVAS_SIZE);
      const q2Info = getQuadrant(c2.x, c2.y, SOURCE_CANVAS_SIZE);
      const q3Info = getQuadrant(c3.x, c3.y, SOURCE_CANVAS_SIZE);
      const q4Info = getQuadrant(c4.x, c4.y, SOURCE_CANVAS_SIZE);
      if (q1Info.label) {
        ctx.fillStyle = quadrantColors.Q1.label;
        ctx.fillText(q1Info.label, mid / 2, mid / 2);
      }
      if (q2Info.label) {
        ctx.fillStyle = quadrantColors.Q2.label;
        ctx.fillText(q2Info.label, mid + mid / 2, mid / 2);
      }
      if (q3Info.label) {
        ctx.fillStyle = quadrantColors.Q3.label;
        ctx.fillText(q3Info.label, mid / 2, mid + mid / 2);
      }
      if (q4Info.label) {
        ctx.fillStyle = quadrantColors.Q4.label;
        ctx.fillText(q4Info.label, mid + mid / 2, mid + mid / 2);
      }

      if (hover) {
        const hy = canvasSize - scaleCoord(hover.y);
        const hx = scaleCoord(hover.x);
        ctx.beginPath();
        ctx.arc(hx, hy, 6, 0, 2 * Math.PI);
        ctx.fillStyle = "rgba(59, 130, 246, 0.3)";
        ctx.fill();
        ctx.strokeStyle = "#3b82f6";
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      // Tap affordance: show helper text when no points have been placed
      if (pointsToDraw.length === 0 && !readOnly) {
        ctx.fillStyle = '#9CA3AF';
        ctx.font = `${compact ? 10 : 12}px system-ui`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(compact ? 'Tap to place' : 'Tap to place your position', canvasSize / 2, canvasSize / 2 - (compact ? 6 : 8));
        if (!compact) {
          ctx.fillText('on the matrix', canvasSize / 2, canvasSize / 2 + 10);
        }
      }

      pointsToDraw.forEach((pt, idx) => {
        const drawX = scaleCoord(pt.x);
        const drawY = canvasSize - scaleCoord(pt.y);
        const quadInfo = getQuadrant(pt.x, pt.y);
        const qColor = quadrantColors[quadInfo.quadrant] || quadrantColors.Q1;
        const ptColor = POINT_COLORS[idx % POINT_COLORS.length];
        const isSelected = pt.id === selectedId;
        const radius = compact ? 5 : 8;
        ctx.beginPath();
        ctx.arc(drawX, drawY, isSelected ? radius + 3 : radius, 0, 2 * Math.PI);
        ctx.fillStyle = "#ffffff";
        ctx.fill();
        ctx.strokeStyle = isSelected ? "#111" : ptColor.border;
        ctx.lineWidth = isSelected ? 4 : 2;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(drawX, drawY, compact ? 3 : 4, 0, 2 * Math.PI);
        ctx.fillStyle = ptColor.border;
        ctx.fill();
        if (content.showPointLabels !== false && (pt.label || pt.quadrantLabel)) {
          ctx.font = compact ? "10px sans-serif" : "12px sans-serif";
          ctx.fillStyle = "#374151";
          ctx.textAlign = "center";
          ctx.textBaseline = "top";
          const label = pt.label || pt.quadrantLabel || `P${idx + 1}`;
          ctx.fillText(label.slice(0, 12), drawX, drawY + (compact ? 6 : 10));
        }
      });
    },
    [canvasSize, compact, getQuadrant, quadrantColors, content.showPointLabels]
  );

  useEffect(() => {
    drawMatrix(points, selectedPointId, hoverPos);
  }, [points, selectedPointId, hoverPos, drawMatrix]);

  useEffect(() => {
    if (!isControlled && normalizedDefault.points.length > 0 && internalPoints.length === 0) {
      setInternalPoints(normalizedDefault.points);
      if (onChange) onChange(content.key, { points: normalizedDefault.points });
    }
  }, []);

  useEffect(() => {
    if (selectPointIdWhenAdded && points.some((p) => p.id === selectPointIdWhenAdded)) {
      setSelectedPointId(selectPointIdWhenAdded);
      setShowFeedback(true);
      // Defer clear so this component's state commits before parent re-renders with selectPointIdWhenAdded=null
      const cb = onDidSelectPoint;
      const id = setTimeout(() => cb?.(), 0);
      return () => clearTimeout(id);
    }
  }, [selectPointIdWhenAdded, points, onDidSelectPoint]);

  const hitTest = (canvasX: number, canvasY: number): MatrixPoint | null => {
    const threshold = compact ? 12 : 18;
    const storedX = toStoredX(canvasX);
    const storedY = toStoredY(canvasSize - canvasY);
    for (let i = points.length - 1; i >= 0; i--) {
      const pt = points[i];
      const dx = scaleCoord(pt.x) - canvasX;
      const dy = canvasSize - scaleCoord(pt.y) - canvasY;
      if (dx * dx + dy * dy <= threshold * threshold) return pt;
    }
    return null;
  };

  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (readOnly || !onChange) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const canvasX = e.clientX - rect.left;
    const canvasY = e.clientY - rect.top;
    const hit = hitTest(canvasX, canvasY);
    if (hit) {
      setSelectedPointId(hit.id);
      setShowFeedback(true);
      drawMatrix(points, hit.id, hoverPos);
      return;
    }
    const x = toStoredX(canvasX);
    const y = toStoredY(canvasSize - canvasY);
    const quad = getQuadrant(x, y);
    const newPoint: MatrixPoint = {
      id: genPointId(),
      x,
      y,
      quadrant: quad.quadrant,
      quadrantLabel: quad.label,
      feedback: "",
    };
    const next = [...points, newPoint];
    updatePoints(next);
    setSelectedPointId(newPoint.id);
    setShowFeedback(true);
  };

  const handleCanvasMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (readOnly || !onChange) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const canvasX = e.clientX - rect.left;
    const canvasY = e.clientY - rect.top;
    const hit = hitTest(canvasX, canvasY);
    if (hit) {
      setDraggingPointId(hit.id);
      dragStartRef.current = {
        x: canvasX,
        y: canvasY,
        pointX: hit.x,
        pointY: hit.y,
      };
    }
  };

  const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const canvasX = e.clientX - rect.left;
    const canvasY = e.clientY - rect.top;
    if (readOnly) {
      setHoverPos(null);
      return;
    }
    if (draggingPointId && dragStartRef.current && onChange) {
      const start = dragStartRef.current;
      const scale = SOURCE_CANVAS_SIZE / canvasSize;
      const deltaStoredX = (canvasX - start.x) * scale;
      const deltaStoredY = -(canvasY - start.y) * scale;
      const newX = Math.round(Math.max(0, Math.min(SOURCE_CANVAS_SIZE, start.pointX + deltaStoredX)));
      const newY = Math.round(Math.max(0, Math.min(SOURCE_CANVAS_SIZE, start.pointY + deltaStoredY)));
      const quad = getQuadrant(newX, newY);
      const next = points.map((p) =>
        p.id === draggingPointId
          ? { ...p, x: newX, y: newY, quadrant: quad.quadrant, quadrantLabel: quad.label }
          : p
      );
      updatePoints(next);
      dragStartRef.current = { x: canvasX, y: canvasY, pointX: newX, pointY: newY };
      return;
    }
    setHoverPos({ x: toStoredX(canvasX), y: toStoredY(canvasSize - canvasY) });
  };

  const handleCanvasMouseUp = () => {
    if (draggingPointId) {
      setDraggingPointId(null);
      dragStartRef.current = null;
    }
  };

  const handleCanvasMouseLeave = () => {
    setHoverPos(null);
    if (draggingPointId) {
      setDraggingPointId(null);
      dragStartRef.current = null;
    }
  };

  const selectedPoint = selectedPointId ? points.find((p) => p.id === selectedPointId) : null;

  const handleFeedbackChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!selectedPointId || readOnly || !onChange) return;
    const next = points.map((p) =>
      p.id === selectedPointId ? { ...p, feedback: e.target.value } : p
    );
    updatePoints(next);
  };

  const handleDeletePoint = () => {
    if (!selectedPointId || !onChange) return;
    const next = points.filter((p) => p.id !== selectedPointId);
    updatePoints(next);
    setSelectedPointId(next.length ? next[next.length - 1].id : null);
    setShowFeedback(next.length > 0);
  };

  const handleLabelChange = (newLabel: string) => {
    if (!selectedPointId || !onChange) return;
    const next = points.map((p) =>
      p.id === selectedPointId ? { ...p, label: newLabel } : p
    );
    updatePoints(next);
  };

  const getFeedbackPrompt = () => {
    if (!selectedPoint) return t("click_to_select") || "Click on the matrix to select";
    const quadInfo = getQuadrant(selectedPoint.x, selectedPoint.y);
    return quadInfo.prompt || t("add_your_thoughts") || "Add your thoughts...";
  };

  const { setNodeRef, isOver } = useDroppable({
    id: `gauge-${content.key}`,
    data: { type: "gauge", gaugeKey: content.key },
  });

  useEffect(() => {
    if (!isOver || !onDragOverPosition) return;
    const el = matrixWrapperRef.current;
    if (!el) return;
    const onMove = (e: PointerEvent) => {
      const rect = el.getBoundingClientRect();
      const relX = (e.clientX - rect.left) / rect.width;
      const relY = (e.clientY - rect.top) / rect.height;
      const x = Math.max(0, Math.min(SOURCE_CANVAS_SIZE, relX * SOURCE_CANVAS_SIZE));
      const y = Math.max(0, Math.min(SOURCE_CANVAS_SIZE, (1 - relY) * SOURCE_CANVAS_SIZE));
      onDragOverPosition(content.key, x, y);
    };
    document.addEventListener("pointermove", onMove);
    return () => document.removeEventListener("pointermove", onMove);
  }, [isOver, onDragOverPosition, content.key]);

  const pointsFromList = connectedListKey
    ? new Set(
        points
          .filter(
            (p) => p.sourceListKey === connectedListKey && p.listItemId != null
          )
          .map((p) => p.listItemId)
      )
    : new Set<number | string>();

  const hasConnectedList =
    connectedListKey && Array.isArray(connectedListItems) && connectedListItems.length > 0;

  return (
    <div
      className={`mt-3 text-start group min-w-0 overflow-hidden max-w-full ${compact ? "w-full" : ""}`}
      dir={direction}
    >
      {showMainLabel && (
        <Label className="block text-sm font-semibold text-gray-800 mb-2">
          {content.label}
        </Label>
      )}

      <div className="rounded-xl bg-white border border-gray-200 shadow-[0_2px_8px_rgba(79,70,229,0.08)] min-w-0 overflow-hidden max-w-full flex flex-col gap-3 p-3 w-full">
        {/* Available items from connected Smart List - drag onto matrix below */}
        {hasConnectedList && (
          <div className="rounded-md bg-white border border-slate-200 min-w-0 overflow-hidden p-2">
            <span className="font-semibold text-gray-700 text-xs block mb-1.5">
              {t("available_items") || "Available Items"}
            </span>
            <p className="text-[10px] text-gray-500 mb-1.5">
              {t("drag_onto_gauge") || "Drag items onto the matrix below to position them."}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {connectedListItems!.map((listItem) => (
                <ConnectedListItem
                  key={`${connectedListKey}-${listItem.id}`}
                  sourceListKey={connectedListKey!}
                  listItemId={listItem.id}
                  text={typeof listItem.text === "string" ? listItem.text : ""}
                  isOnGauge={pointsFromList.has(listItem.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Gauge: Y-axis + canvas + X-axis — droppable so list items can be dropped here */}
        <div
          ref={(el) => {
            setNodeRef(el);
            matrixWrapperRef.current = el;
          }}
          className={`flex-shrink-0 flex flex-col items-center rounded-lg transition-shadow ${isOver ? "ring-2 ring-primary ring-offset-2 ring-offset-transparent" : ""}`}
        >
          <div className="flex items-center gap-2">
            {(yLowLabel || yHighLabel) ? (
              <div
                className="flex flex-shrink-0 items-center justify-center text-gray-600"
                style={{
                  width: "24px",
                  height: canvasSize,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {yHighLabel ? (
                  <div
                    className="font-semibold text-[10px]"
                    style={{
                      writingMode: "vertical-rl",
                      transform: "rotate(180deg)",
                      textAlign: "center",
                    }}
                  >
                    {yHighLabel}
                  </div>
                ) : null}
                {yLowLabel ? (
                  <div
                    className="font-semibold text-[10px]"
                    style={{
                      writingMode: "vertical-rl",
                      transform: "rotate(180deg)",
                      textAlign: "center",
                    }}
                  >
                    {yLowLabel}
                  </div>
                ) : null}
              </div>
            ) : null}
            <div className="flex flex-col items-center">
              <canvas
                ref={canvasRef}
                width={canvasSize}
                height={canvasSize}
                className={`border-2 border-slate-300 rounded-lg bg-white shadow-md block transition-shadow ${
                  readOnly ? "cursor-default" : "cursor-crosshair hover:shadow-lg"
                }`}
                onClick={handleCanvasClick}
                onMouseDown={handleCanvasMouseDown}
                onMouseMove={handleCanvasMouseMove}
                onMouseUp={handleCanvasMouseUp}
                onMouseLeave={handleCanvasMouseLeave}
              />
              {(xLowLabel || xHighLabel) ? (
                <div className="flex justify-between w-full text-[10px] font-semibold text-gray-600 mt-1 px-0.5">
                  <span>{xLowLabel || ""}</span>
                  <span>{xHighLabel || ""}</span>
                </div>
              ) : (
                <div className="text-xs font-semibold text-gray-600 text-center mt-1">{xAxisLabel}</div>
              )}
            </div>
          </div>
        </div>

        {/* All text content below the gauge */}
        <div className="flex flex-col min-w-0 overflow-hidden gap-2">
          {content.showPointsLegend !== false && points.length > 0 && (
            <div className="rounded-md bg-white border border-slate-200 min-w-0 overflow-hidden p-2">
              <span className="font-semibold text-gray-700 text-xs block truncate">
                {t("points") || "Points"}
              </span>
              <ul className="mt-0.5 space-y-0.5 min-w-0 text-xs">
                {points.map((pt, idx) => (
                  <li
                    key={pt.id}
                    className={`flex items-center gap-2 cursor-pointer min-w-0 ${pt.id === selectedPointId ? "font-semibold" : ""}`}
                    onClick={() => {
                      setSelectedPointId(pt.id);
                      setShowFeedback(true);
                    }}
                  >
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{
                        backgroundColor: POINT_COLORS[idx % POINT_COLORS.length].border,
                      }}
                    />
                    <span className="truncate min-w-0">{pt.label || pt.quadrantLabel || `P${idx + 1}`}</span>
                    {content.showCoordinates !== false && (
                      <span className="text-gray-500 font-mono flex-shrink-0 text-[10px]">({Math.round(Number(pt.x))},{Math.round(Number(pt.y))})</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {selectedPoint ? (
            <div className="rounded-md bg-white border border-slate-200 min-w-0 overflow-hidden p-2">
              <div className="flex items-center gap-2 min-w-0 flex-wrap">
                <span className="font-semibold text-gray-700 text-xs flex-shrink-0">
                  {t("selected_quadrant") || "Selected Quadrant"}:
                </span>
                <span
                  className="px-2 py-0.5 rounded font-bold text-xs truncate min-w-0 max-w-full"
                  style={{
                    backgroundColor: quadrantColors[selectedPoint.quadrant]?.bg || "transparent",
                    color: quadrantColors[selectedPoint.quadrant]?.label || "#000",
                  }}
                  title={selectedPoint.quadrantLabel}
                >
                  {selectedPoint.quadrantLabel}
                </span>
              </div>
              {content.showCoordinates !== false && (
                <div className="text-gray-500 text-[10px] flex flex-wrap gap-x-3 gap-y-0 mt-0.5">
                  <span className="whitespace-nowrap">
                    {xAxisLabel}: <span className="font-mono font-semibold">{Math.round(Number(selectedPoint.x))}</span>
                  </span>
                  <span className="whitespace-nowrap">
                    {yAxisLabel}: <span className="font-mono font-semibold">{Math.round(Number(selectedPoint.y))}</span>
                  </span>
                </div>
              )}
              {!readOnly && (
                <div className="mt-2 flex items-center gap-2 min-w-0">
                  <input
                    type="text"
                    className="flex-1 min-w-0 text-sm border rounded px-2 py-1 max-w-full"
                    placeholder={t("point_label") || "Point label"}
                    value={selectedPoint.label || ""}
                    onChange={(e) => handleLabelChange(e.target.value)}
                  />
                  <button
                    type="button"
                    className="text-red-600 hover:text-red-800 text-sm flex-shrink-0"
                    onClick={handleDeletePoint}
                  >
                    {t("delete") || "Delete"}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-md bg-primary/5 border border-primary/20 text-center min-w-0 overflow-hidden p-2">
              <p className="text-primary text-xs">
                {t("click_matrix_to_select") || "Click on the matrix to make your selection"}
              </p>
            </div>
          )}

          {showFeedback && selectedPoint && (
            <div className="flex flex-col min-w-0 overflow-hidden mt-4 pt-4 border-t border-gray-100">
              <Label className="font-semibold text-gray-700 text-xs mb-1 truncate">
                {getFeedbackPrompt()}
              </Label>
              <AutogrowTextarea
                minHeight={48}
                value={selectedPoint.feedback || ""}
                onChange={handleFeedbackChange}
                placeholder={t("enter_feedback_here") || "Enter your thoughts here..."}
                className="min-w-0 max-w-full"
                readOnly={readOnly}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MatrixGauge;
